import { debounce } from '../utils';

const MM = window.matchMedia;

class MatchMedia {

    /**
     * Media rules
     */
    public readonly rules: TRules;

    /**
     * Registered listeners
     *
     * @protected
     */
    protected listeners: any[] = [];

    /**
     * @param rules
     */
    public constructor(rules: TRules) {
        const that = this;

        if (typeof MM !== 'function') {
            console && console.warn('window.matchMedia() not found.');
            return;
        }

        this.rules = rules;

        for (const key in rules) {
            let mql = MM(rules[key]);
            mql.addEventListener('change', (e: MediaQueryListEvent) => that.notify(e, key));
        }
    }


    /**
     * Adds listener for the match media events
     *
     * @param listener
     * @param immediate
     */
    public listen(listener: TListener, immediate: boolean = true): () => void {

        const that = this;
        const rules = that.rules;

        // debounce listener to fire only when calculations are done
        listener = debounce(listener, 1);

        // check if rules matches
        const matches: any = {};

        for (const key in rules) {
            matches[key] = MM(rules[key]).matches;
        }

        // call listener (initial match)
        if (immediate) {
            listener(matches, rules);
        }

        // register listener with keys matched on init
        const listenerArr = [listener, matches];
        that.listeners.push(listenerArr);

        // return unlisten function
        return function unlisten() {
            that.listeners.splice(that.listeners.indexOf(listenerArr), 1);
        }
    }

    /**
     * Notifies listeners on changes
     *
     * @param e
     * @param ruleKey
     */
    protected notify = (e: MediaQueryListEvent, ruleKey: string) => {
        const that = this;

        for (let i = 0; i < that.listeners.length; i++) {
            const matches = that.listeners[i][1];
            matches[ruleKey] = e.matches;

            that.listeners[i][0](matches, that.rules);
        }
    };
}

/*
 * {
 *     'k1': '(max-width:575px)',
 *     'k2': '(min-width:576px) and (max-width:767px)',
 *     'k3': 'all and (orientation: landscape)',
 *  }
 */
type TRules = {
    [key: string]: string;
};

type TMatches = {
    [key: string]: boolean;
};

type TListener = (matches: TMatches, rules: TRules) => void;

export default MatchMedia;
export { TListener, TMatches, TRules }
